import {useContext} from 'react';

import {Formik} from 'formik';
import {useTranslation} from 'react-i18next';

import JobSearchPreferences from '../JobSearchPreferences/JobSearchPreferences';
import LinkedinConnection from '../LinkedinConnection';
import OnboardingComplete from '../OnboardingComplete';
import Qualifications from '../Qualifications/Qualifications';

import {SnackBarContext} from '../../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../../contexts/UserContext/UserContext';
import {ApplicantRoleData} from '../../../../../types/User';
import {scrollToError} from '../../../../../utils/scrollToError';
import {ApplicantOnboardingService} from '../../services/ApplicantOnboardingService';

export default function ApplicantOnboardingPage() {
  const {t} = useTranslation();
  const {currentUser, userContextLogin} = useContext(UserContext);
  const applicantId = (currentUser?.role.data as ApplicantRoleData).applicantId;
  const {dispatchMessage} = useContext(SnackBarContext);

  return (
    <>
      <Formik
        initialValues={{
          applicantOnboarding: {
            dreamJobDescription: '',
            jobSearchPreferences: {
              earliestStartDate: '',
              locationType: '',
              baseCountry: {
                isoCode: '',
                value: '',
              },
              preferredLocation: {
                country: {
                  isoCode: '',
                  value: '',
                },
                cities: [],
                proximitySearch: {
                  value: null,
                  radiusInKilometers: null,
                },
              },
            },
            qualifications: {
              leadershipExperience: false,
              professionalExperiences: [],
            },
          },
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validate={(values) => {
          const errors: any = {};

          if (!values.applicantOnboarding.jobSearchPreferences.locationType) {
            errors.applicantOnboarding = {
              ...errors.applicantOnboarding,
              jobSearchPreferences: {
                ...errors.applicantOnboarding?.jobSearchPreferences,
                locationType: t('applicantOnboarding.jobSearchPreferences.formErrors.locationType'),
              },
            };
          }

          // If the location is "anywhere" than check for the base country else check for the preferred location country
          if (values.applicantOnboarding.jobSearchPreferences.locationType === 'anywhere') {
            if (!values.applicantOnboarding.jobSearchPreferences.baseCountry.isoCode) {
              errors.applicantOnboarding = {
                ...errors.applicantOnboarding,
                jobSearchPreferences: {
                  ...errors.applicantOnboarding?.jobSearchPreferences,
                  baseCountry: {
                    ...errors.applicantOnboarding?.jobSearchPreferences?.baseCountry,
                    isoCode: t('applicantOnboarding.jobSearchPreferences.formErrors.baseCountry'),
                  },
                },
              };
            }
          } else {
            if (!values.applicantOnboarding.jobSearchPreferences.preferredLocation.country.isoCode) {
              errors.applicantOnboarding = {
                ...errors.applicantOnboarding,
                jobSearchPreferences: {
                  ...errors.applicantOnboarding?.jobSearchPreferences,
                  preferredLocation: {
                    ...errors.applicantOnboarding?.jobSearchPreferences?.preferredLocation,
                    country: {
                      ...errors.applicantOnboarding?.jobSearchPreferences?.preferredLocation?.country,
                      isoCode: t('applicantOnboarding.jobSearchPreferences.formErrors.preferredLocation.country'),
                    },
                  },
                },
              };
            }
          }

          // If the location is "specific" than check for the preferred location cities
          if (values.applicantOnboarding.jobSearchPreferences.locationType === 'specific') {
            if (
              values.applicantOnboarding.jobSearchPreferences.preferredLocation.cities.length === 0 &&
              !values.applicantOnboarding.jobSearchPreferences.preferredLocation.proximitySearch.value
            ) {
              errors.applicantOnboarding = {
                ...errors.applicantOnboarding,
                jobSearchPreferences: {
                  ...errors.applicantOnboarding?.jobSearchPreferences,
                  preferredLocation: {
                    ...errors.applicantOnboarding?.jobSearchPreferences?.preferredLocation,
                    cities: t('applicantOnboarding.jobSearchPreferences.formErrors.preferredLocation.cities'),
                  },
                },
              };
            }

            const zipCode = values.applicantOnboarding
              .jobSearchPreferences.preferredLocation.proximitySearch.value ?? '';

            if (
              values.applicantOnboarding.jobSearchPreferences.preferredLocation.cities.length &&
              values.applicantOnboarding.jobSearchPreferences.preferredLocation.cities.find((id) => id === 'others') &&
              !/^\d{5}(-\d{4})?$/.test(zipCode.toString())
            ) {
              errors.applicantOnboarding = {
                ...errors.applicantOnboarding,
                jobSearchPreferences: {
                  ...errors.applicantOnboarding?.jobSearchPreferences,
                  preferredLocation: {
                    ...errors.applicantOnboarding?.jobSearchPreferences?.preferredLocation,
                    proximitySearch: {
                      ...errors.applicantOnboarding?.jobSearchPreferences?.preferredLocation?.proximitySearch,
                      value: t('applicantOnboarding.jobSearchPreferences.formErrors.preferredLocation.zipCode'),
                    },
                  },
                },
              };
            }
          }

          // At least one area of expertise must be selected
          if (values.applicantOnboarding.qualifications.professionalExperiences.length === 0) {
            errors.applicantOnboarding = {
              ...errors.applicantOnboarding,
              qualifications: {
                areasOfExpertise: t('applicantOnboarding.qualifications.formErrors.areasOfExpertise'),
              },
            };
          }

          if (errors.applicantOnboarding) {
            scrollToError(Object.keys(errors.applicantOnboarding)[0]);
          }
          return errors;
        }}
        onSubmit={(values, {setSubmitting, validateForm}) => {
          validateForm(values).then(() => {
            // removes others value before sending data to BE.
            values.applicantOnboarding.jobSearchPreferences.preferredLocation.cities =
              values.applicantOnboarding.jobSearchPreferences.preferredLocation.cities.filter((id) => id !== 'others');
            setTimeout(() => {
              ApplicantOnboardingService.complete(applicantId, values as any).then((response) => {
                if (response.ok) {
                  if (response.data) {
                    const upddatedApplicantRoleData = {...currentUser?.role.data, onboardingCompleted: true};

                    const updatedCurrentUser = {
                      ...currentUser,
                      role: {...currentUser?.role, data: upddatedApplicantRoleData},
                    };

                    userContextLogin(updatedCurrentUser as any);
                  }
                } else {
                  dispatchMessage(
                    t('shared.formSubmit.error', response.error?.extra_details || 'Undefined error'),
                    'error'
                  );
                }
              });

              setSubmitting(false);
            }, 400);
          });
        }}
      >
        {({setFieldValue, values, handleSubmit, errors}) => (
          <form onSubmit={handleSubmit}>
            <JobSearchPreferences formik={{values, setFieldValue}} />
            <Qualifications formik={{values, setFieldValue, errors}} />
            <LinkedinConnection />
            <OnboardingComplete formik={{values, setFieldValue}} />
          </form>
        )}
      </Formik>
    </>
  );
}
